import React, { useState, useMemo } from 'react';
import { THEME } from '../../styles/theme';
import {
  getTrades, saveTrade, updateTradeResult, deleteTrade,
  getRules, getRiskSettings, getCurrentPrice,
} from '../../services/storage';
import { getCurrentSession } from '../../utils/dateUtils';

// ── Styles ──────────────────────────────────────────────────────────────
const S = {
  page:  { padding: '20px', maxWidth: '100%', paddingBottom: '40px' },
  title: { fontSize: '22px', fontWeight: '700', color: THEME.colors.blue, letterSpacing: '2px', fontFamily: THEME.fonts.hud, marginBottom: '4px' },
  sub:   { fontSize: '13px', color: THEME.colors.textMuted, marginBottom: '20px' },
  tabs:  { display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' },
  tab: (a) => ({
    padding: '8px 18px', borderRadius: '20px', cursor: 'pointer', fontSize: '13px', fontWeight: '700',
    border:  a ? `1px solid ${THEME.colors.blue}` : '1px solid rgba(255,255,255,0.1)',
    background: a ? 'rgba(0,212,255,0.1)' : 'transparent',
    color:   a ? THEME.colors.blue : THEME.colors.textMuted,
  }),
  card:  (border) => ({ background: 'rgba(255,255,255,0.03)', border: `1px solid ${border || 'rgba(255,255,255,0.08)'}`, borderRadius: '12px', padding: '16px', marginBottom: '12px' }),
  label: { fontSize: '11px', color: THEME.colors.textMuted, fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '5px' },
  input: { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: THEME.colors.text, fontSize: '14px', padding: '9px 11px', outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: THEME.fonts.hud },
  row:   { display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' },
  badge: (color, bg) => ({ padding: '2px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700', color, background: bg, display: 'inline-block', fontFamily: THEME.fonts.hud }),
  sectionTitle: { fontSize: '10px', fontWeight: '700', letterSpacing: '2px', color: THEME.colors.textMuted, fontFamily: THEME.fonts.hud, marginBottom: '10px', textTransform: 'uppercase' },
  btn: (v) => ({
    padding: '9px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer',
    fontSize: '12px', fontWeight: '700', fontFamily: THEME.fonts.hud,
    background: v === 'primary' ? THEME.colors.blue : v === 'green' ? THEME.colors.green : v === 'danger' ? 'rgba(255,51,51,0.15)' : v === 'gold' ? THEME.colors.gold : 'rgba(255,255,255,0.07)',
    color: ['primary','green','gold'].includes(v) ? '#000' : v === 'danger' ? THEME.colors.red : THEME.colors.textMuted,
  }),
};

const RESULT_CONFIG = {
  WIN:     { color: THEME.colors.green, bg: 'rgba(0,255,136,0.12)',  label: '✅ WIN' },
  LOSS:    { color: THEME.colors.red,   bg: 'rgba(255,51,51,0.12)',  label: '❌ LOSS' },
  BE:      { color: THEME.colors.gold,  bg: 'rgba(255,215,0,0.10)', label: '⚡ BREAK EVEN' },
  PARTIAL: { color: THEME.colors.blue,  bg: 'rgba(0,212,255,0.10)', label: '⚙️ PARTIAL' },
  OPEN:    { color: THEME.colors.textMuted, bg: 'rgba(255,255,255,0.04)', label: '🔵 OPEN' },
};

const GRADE_COLORS = {
  'A+': '#00ff88', A: '#00d4ff', B: '#ffaa00', C: '#ff6600', D: '#ff3333',
};

function Field({ label, children, style }) {
  return (
    <div style={{ flex: 1, minWidth: '120px', ...style }}>
      <div style={S.label}>{label}</div>
      {children}
    </div>
  );
}

function Inp({ label, style, ...props }) {
  return (
    <Field label={label} style={style}>
      <input style={S.input} {...props} />
    </Field>
  );
}

function Sel({ label, style, children, ...props }) {
  return (
    <Field label={label} style={style}>
      <select style={S.input} {...props}>{children}</select>
    </Field>
  );
}

// ── Stats Bar ────────────────────────────────────────────────────────────
function StatsBar({ trades }) {
  const closed = trades.filter(t => t.result && t.result !== 'OPEN');
  const wins   = closed.filter(t => t.result === 'WIN').length;
  const open   = trades.filter(t => !t.result || t.result === 'OPEN').length;
  const pnl    = closed.reduce((s, t) => s + (parseFloat(t.pnl_usd) || 0), 0);
  const avgRR  = closed.length > 0
    ? (closed.reduce((s, t) => s + (parseFloat(t.rr_achieved) || 0), 0) / closed.length).toFixed(1)
    : '—';
  const wr     = closed.length > 0 ? Math.round((wins / closed.length) * 100) : 0;

  const items = [
    { label: 'Total',    value: trades.length,         color: THEME.colors.text },
    { label: 'Open',     value: open,                  color: THEME.colors.blue },
    { label: 'Win Rate', value: `${wr}%`,              color: wr >= 60 ? THEME.colors.green : wr >= 45 ? THEME.colors.gold : THEME.colors.red },
    { label: 'Wins',     value: wins,                  color: THEME.colors.green },
    { label: 'Losses',   value: closed.length - wins,  color: THEME.colors.red },
    { label: 'Avg R:R',  value: avgRR,                 color: THEME.colors.gold },
    { label: 'P&L',      value: `$${pnl.toFixed(0)}`, color: pnl >= 0 ? THEME.colors.green : THEME.colors.red },
  ];

  return (
    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '18px' }}>
      {items.map(it => (
        <div key={it.label} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '10px', padding: '10px 14px', textAlign: 'center', flex: 1, minWidth: '70px' }}>
          <div style={{ fontSize: '18px', fontWeight: '900', color: it.color, fontFamily: THEME.fonts.hud }}>{it.value}</div>
          <div style={{ fontSize: '10px', color: THEME.colors.textMuted, marginTop: '2px' }}>{it.label}</div>
        </div>
      ))}
    </div>
  );
}

// ── New Trade Form ───────────────────────────────────────────────────────
function NewTradeForm({ onSaved }) {
  const riskSettings = getRiskSettings();
  const rules        = getRules().filter(r => r.status === 'active');
  const livePrice    = getCurrentPrice();
  const session      = getCurrentSession();

  const [f, setF] = useState({
    direction: 'BUY', entry: livePrice > 0 ? livePrice.toFixed(2) : '',
    sl: '', tp1: '', tp2: '', lot_size: '0.01',
    setup_grade: 'B', ict_concept: 'OB', htf_bias: 'Bullish',
    confluence_score: '5', psychology_before: '7',
    notes: '', rules_used: [], followed_rules: true,
  });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  // Auto R:R
  const rrPlanned = useMemo(() => {
    const e = parseFloat(f.entry), s = parseFloat(f.sl), t = parseFloat(f.tp1);
    if (!e || !s || !t || Math.abs(e - s) === 0) return 0;
    return parseFloat((Math.abs(t - e) / Math.abs(e - s)).toFixed(2));
  }, [f.entry, f.sl, f.tp1]);

  // Auto lot size from risk
  const autoLot = useMemo(() => {
    const e = parseFloat(f.entry), s = parseFloat(f.sl);
    if (!e || !s || Math.abs(e - s) === 0) return null;
    const riskAmt = riskSettings.account_balance * riskSettings.risk_per_trade_percent / 100;
    const slPips  = Math.abs(e - s) * 10;
    const lot     = riskAmt / (slPips * 0.10);
    return Math.max(0.01, Math.min(parseFloat(lot.toFixed(2)), 10));
  }, [f.entry, f.sl, riskSettings]);

  const handleSubmit = () => {
    if (!f.entry || !f.sl || !f.tp1) { alert('Entry, SL and TP1 are required'); return; }
    saveTrade({
      ...f,
      rr_planned:  rrPlanned,
      lot_size:    parseFloat(f.lot_size),
      entry:       parseFloat(f.entry),
      sl:          parseFloat(f.sl),
      tp1:         parseFloat(f.tp1),
      tp2:         parseFloat(f.tp2) || 0,
      confluence_score: parseFloat(f.confluence_score),
      psychology_before: parseInt(f.psychology_before),
      result:      'OPEN',
    });
    onSaved();
  };

  const psyColor = parseInt(f.psychology_before) >= 7 ? THEME.colors.green : parseInt(f.psychology_before) >= 5 ? THEME.colors.gold : THEME.colors.red;

  return (
    <div style={S.card('rgba(0,212,255,0.2)')}>
      <div style={S.sectionTitle}>NEW TRADE ENTRY</div>

      {/* Direction */}
      <div style={{ ...S.row, marginBottom: '14px' }}>
        {['BUY','SELL'].map(d => (
          <button key={d} onClick={() => set('direction', d)} style={{
            flex: 1, padding: '12px', borderRadius: '10px', border: 'none', cursor: 'pointer',
            fontSize: '15px', fontWeight: '900', fontFamily: THEME.fonts.hud,
            background: f.direction === d ? (d === 'BUY' ? THEME.colors.green : THEME.colors.red) : 'rgba(255,255,255,0.05)',
            color: f.direction === d ? '#000' : THEME.colors.textMuted,
          }}>
            {d === 'BUY' ? '▲ BUY' : '▼ SELL'}
          </button>
        ))}
      </div>

      {/* Prices */}
      <div style={S.row}>
        <Inp label="Entry Price" type="number" step="0.01" value={f.entry} onChange={e => set('entry', e.target.value)} />
        <Inp label="Stop Loss"   type="number" step="0.01" value={f.sl}    onChange={e => set('sl',    e.target.value)} />
        <Inp label="TP1 (50%)"   type="number" step="0.01" value={f.tp1}   onChange={e => set('tp1',   e.target.value)} />
        <Inp label="TP2 (run)"   type="number" step="0.01" value={f.tp2}   onChange={e => set('tp2',   e.target.value)} />
      </div>

      {/* R:R + lot */}
      <div style={S.row}>
        <div style={{ flex: 1, minWidth: '120px', background: 'rgba(0,0,0,0.3)', borderRadius: '8px', padding: '10px 14px' }}>
          <div style={S.label}>Planned R:R</div>
          <div style={{ fontSize: '22px', fontWeight: '900', color: rrPlanned >= 2 ? THEME.colors.green : rrPlanned >= 1.5 ? THEME.colors.gold : THEME.colors.red, fontFamily: THEME.fonts.hud }}>
            {rrPlanned > 0 ? `1:${rrPlanned}` : '—'}
          </div>
        </div>
        <div style={{ flex: 1, minWidth: '120px', background: 'rgba(0,0,0,0.3)', borderRadius: '8px', padding: '10px 14px' }}>
          <div style={S.label}>Auto Lot ({riskSettings.risk_per_trade_percent}% risk)</div>
          <div style={{ fontSize: '22px', fontWeight: '900', color: THEME.colors.gold, fontFamily: THEME.fonts.hud }}>
            {autoLot !== null ? autoLot : '—'}
          </div>
        </div>
        <Inp label="Lot Size (override)" style={{ flex: 1 }} type="number" step="0.01" value={f.lot_size} onChange={e => set('lot_size', e.target.value)} />
      </div>

      {/* Setup details */}
      <div style={S.row}>
        <Sel label="Setup Grade" value={f.setup_grade} onChange={e => set('setup_grade', e.target.value)}>
          {['A+','A','B','C','D'].map(g => <option key={g} value={g}>{g}</option>)}
        </Sel>
        <Sel label="ICT Concept" value={f.ict_concept} onChange={e => set('ict_concept', e.target.value)}>
          {['OB','FVG','Liquidity Sweep','BOS','CHoCH','OTE','AMD','Multiple'].map(v => <option key={v} value={v}>{v}</option>)}
        </Sel>
        <Sel label="HTF Bias" value={f.htf_bias} onChange={e => set('htf_bias', e.target.value)}>
          <option value="Bullish">Bullish</option>
          <option value="Bearish">Bearish</option>
          <option value="Neutral">Neutral</option>
        </Sel>
        <Inp label="Confluence Score" type="number" min="0" max="10" step="0.1" value={f.confluence_score} onChange={e => set('confluence_score', e.target.value)} />
      </div>

      {/* Psychology */}
      <div style={{ marginBottom: '10px' }}>
        <div style={S.label}>Psychology Before Trade: <span style={{ color: psyColor }}>{f.psychology_before}/10</span></div>
        <input type="range" min="1" max="10" value={f.psychology_before} onChange={e => set('psychology_before', e.target.value)}
          style={{ width: '100%', accentColor: psyColor }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: THEME.colors.textMuted }}>
          <span>1 — Terrible</span><span>5 — Okay</span><span>10 — Perfect</span>
        </div>
      </div>

      {/* Session */}
      <div style={{ fontSize: '12px', color: THEME.colors.textMuted, marginBottom: '10px' }}>
        Session: <strong style={{ color: session.quality === 'HIGH' || session.quality === 'PEAK' ? THEME.colors.green : THEME.colors.textMuted }}>
          {session.emoji} {session.name} ({session.quality})
        </strong>
        {(session.quality === 'LOW') && <span style={{ color: THEME.colors.gold }}> ⚠️ Low quality session — are you sure?</span>}
      </div>

      {/* Rules used */}
      {rules.length > 0 && (
        <div style={{ marginBottom: '10px' }}>
          <div style={S.label}>Rules Used (select all that apply)</div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {rules.slice(0, 8).map(r => (
              <button key={r.id} onClick={() => set('rules_used', f.rules_used.includes(r.id) ? f.rules_used.filter(x => x !== r.id) : [...f.rules_used, r.id])} style={{
                padding: '5px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: '700',
                background: f.rules_used.includes(r.id) ? THEME.colors.blue : 'rgba(255,255,255,0.05)',
                color: f.rules_used.includes(r.id) ? '#000' : THEME.colors.textMuted,
              }}>
                {r.id}: {r.title?.substring(0, 20)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Notes */}
      <textarea style={{ ...S.input, resize: 'vertical', minHeight: '60px', marginBottom: '12px' }}
        placeholder="Trade rationale — what did you see? (OB at 4024, SSL swept, London open...)"
        value={f.notes} onChange={e => set('notes', e.target.value)} />

      {/* Submit */}
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
        <button style={S.btn('primary')} onClick={handleSubmit}>Log Trade</button>
        {parseInt(f.psychology_before) < 6 && (
          <span style={S.badge(THEME.colors.red, 'rgba(255,51,51,0.1)')}>⚠️ Low psychology — consider skipping</span>
        )}
        {rrPlanned > 0 && rrPlanned < 1.5 && (
          <span style={S.badge(THEME.colors.gold, 'rgba(255,215,0,0.1)')}>⚠️ R:R below 1.5</span>
        )}
      </div>
    </div>
  );
}

// ── Close Trade inline form ──────────────────────────────────────────────
function CloseTradeForm({ trade, onClosed, onCancel }) {
  const [f, setF] = useState({ result: 'WIN', exit_price: '', psychology_after: '7', followed_rules: 'yes', lessons_learned: '' });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const pnl = useMemo(() => {
    const ex = parseFloat(f.exit_price), en = parseFloat(trade.entry);
    const lot = parseFloat(trade.lot_size) || 0.01;
    if (!ex || !en) return null;
    const dir = trade.direction === 'BUY' ? 1 : -1;
    return parseFloat((dir * (ex - en) * lot * 100).toFixed(2));
  }, [f.exit_price, trade]);

  const rrAchieved = useMemo(() => {
    const ex = parseFloat(f.exit_price), en = parseFloat(trade.entry), sl = parseFloat(trade.sl);
    if (!ex || !en || !sl || Math.abs(en - sl) === 0) return null;
    const dir = trade.direction === 'BUY' ? 1 : -1;
    return parseFloat((dir * (ex - en) / Math.abs(en - sl)).toFixed(2));
  }, [f.exit_price, trade]);

  const handleClose = () => {
    if (!f.exit_price) { alert('Exit price required'); return; }
    updateTradeResult(trade.id, {
      result:            f.result,
      exit_price:        parseFloat(f.exit_price),
      psychology_after:  parseInt(f.psychology_after),
      followed_rules:    f.followed_rules === 'yes',
      lessons_learned:   f.lessons_learned,
      pnl_usd:           pnl,
      rr_achieved:       rrAchieved,
      jarvis_was_correct: null,
    });
    onClosed();
  };

  const resultColors = { WIN: THEME.colors.green, LOSS: THEME.colors.red, BE: THEME.colors.gold, PARTIAL: THEME.colors.blue };

  return (
    <div style={{ marginTop: '12px', padding: '14px', background: 'rgba(0,0,0,0.3)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)' }}>
      <div style={S.sectionTitle}>CLOSE TRADE — {trade.id}</div>
      <div style={S.row}>
        <Field label="Result">
          <div style={{ display: 'flex', gap: '6px' }}>
            {['WIN','LOSS','BE','PARTIAL'].map(r => (
              <button key={r} onClick={() => set('result', r)} style={{
                flex: 1, padding: '6px 4px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                fontSize: '11px', fontWeight: '700',
                background: f.result === r ? resultColors[r] : 'rgba(255,255,255,0.05)',
                color: f.result === r ? '#000' : THEME.colors.textMuted,
              }}>{r}</button>
            ))}
          </div>
        </Field>
        <Inp label="Exit Price" type="number" step="0.01" value={f.exit_price} onChange={e => set('exit_price', e.target.value)} />
        <Field label="Followed Rules?">
          <div style={{ display: 'flex', gap: '6px' }}>
            {[['yes', '✅ Yes'], ['no', '❌ No']].map(([v, l]) => (
              <button key={v} onClick={() => set('followed_rules', v)} style={{
                flex: 1, padding: '6px 4px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: '700',
                background: f.followed_rules === v ? (v === 'yes' ? THEME.colors.green : THEME.colors.red) : 'rgba(255,255,255,0.05)',
                color: f.followed_rules === v ? '#000' : THEME.colors.textMuted,
              }}>{l}</button>
            ))}
          </div>
        </Field>
      </div>

      {/* P&L preview */}
      {pnl !== null && (
        <div style={{ display: 'flex', gap: '12px', marginBottom: '10px' }}>
          <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '8px', padding: '8px 14px' }}>
            <div style={{ fontSize: '11px', color: THEME.colors.textMuted }}>P&L</div>
            <div style={{ fontSize: '20px', fontWeight: '900', color: pnl >= 0 ? THEME.colors.green : THEME.colors.red, fontFamily: THEME.fonts.hud }}>
              {pnl >= 0 ? '+' : ''}${pnl}
            </div>
          </div>
          {rrAchieved !== null && (
            <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '8px', padding: '8px 14px' }}>
              <div style={{ fontSize: '11px', color: THEME.colors.textMuted }}>R:R Achieved</div>
              <div style={{ fontSize: '20px', fontWeight: '900', color: rrAchieved >= 1.5 ? THEME.colors.green : THEME.colors.red, fontFamily: THEME.fonts.hud }}>
                {rrAchieved >= 0 ? `1:${rrAchieved}` : rrAchieved}
              </div>
            </div>
          )}
        </div>
      )}

      <div style={{ marginBottom: '10px' }}>
        <div style={S.label}>Psychology After: {f.psychology_after}/10</div>
        <input type="range" min="1" max="10" value={f.psychology_after} onChange={e => set('psychology_after', e.target.value)}
          style={{ width: '100%', accentColor: THEME.colors.blue }} />
      </div>

      <textarea style={{ ...S.input, resize: 'vertical', minHeight: '55px', marginBottom: '10px' }}
        placeholder="What did you learn from this trade?"
        value={f.lessons_learned} onChange={e => set('lessons_learned', e.target.value)} />

      <div style={{ display: 'flex', gap: '8px' }}>
        <button style={S.btn(f.result === 'WIN' ? 'green' : f.result === 'LOSS' ? 'danger' : 'gold')} onClick={handleClose}>
          Confirm Close
        </button>
        <button style={S.btn()} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ── Trade Card ───────────────────────────────────────────────────────────
function TradeCard({ trade, onUpdate }) {
  const [closing, setClosing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const isOpen  = !trade.result || trade.result === 'OPEN';
  const res     = RESULT_CONFIG[trade.result] || RESULT_CONFIG.OPEN;
  const gradeColor = GRADE_COLORS[trade.setup_grade] || THEME.colors.textMuted;

  return (
    <div style={S.card(isOpen ? 'rgba(0,212,255,0.2)' : (trade.result === 'WIN' ? 'rgba(0,255,136,0.15)' : trade.result === 'LOSS' ? 'rgba(255,51,51,0.12)' : 'rgba(255,255,255,0.07)'))}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '8px' }}>
        <span style={{ fontSize: '13px', fontWeight: '800', color: THEME.colors.blue, fontFamily: THEME.fonts.hud }}>{trade.id}</span>
        <span style={S.badge(trade.direction === 'BUY' ? '#000' : '#fff', trade.direction === 'BUY' ? THEME.colors.green : THEME.colors.red)}>
          {trade.direction === 'BUY' ? '▲ BUY' : '▼ SELL'}
        </span>
        <span style={S.badge(res.color, res.bg)}>{res.label}</span>
        <span style={S.badge(gradeColor, `${gradeColor}18`)}>{trade.setup_grade}</span>
        <span style={S.badge(THEME.colors.textMuted, 'rgba(255,255,255,0.05)')}>{trade.session}</span>
        <span style={{ fontSize: '11px', color: THEME.colors.textMuted }}>{trade.date} {trade.day_of_week}</span>
        <span style={{ flex: 1 }} />
        <button style={{ ...S.btn(), padding: '4px 10px', fontSize: '11px' }} onClick={() => setExpanded(e => !e)}>
          {expanded ? '▲ Less' : '▼ More'}
        </button>
        {isOpen && (
          <button style={{ ...S.btn('green'), padding: '5px 12px' }} onClick={() => setClosing(c => !c)}>
            {closing ? 'Cancel' : 'Close Trade'}
          </button>
        )}
        <button style={{ ...S.btn('danger'), padding: '4px 10px', fontSize: '11px' }} onClick={() => { if (window.confirm(`Delete ${trade.id}?`)) { deleteTrade(trade.id); onUpdate(); } }}>
          ✕
        </button>
      </div>

      {/* Price line */}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', fontSize: '13px', marginBottom: '6px' }}>
        <span>Entry: <strong style={{ color: THEME.colors.gold, fontFamily: THEME.fonts.hud }}>{trade.entry}</strong></span>
        <span>SL: <strong style={{ color: THEME.colors.red }}>{trade.sl}</strong></span>
        <span>TP1: <strong style={{ color: THEME.colors.green }}>{trade.tp1}</strong></span>
        {trade.tp2 > 0 && <span>TP2: <strong style={{ color: THEME.colors.green }}>{trade.tp2}</strong></span>}
        {trade.rr_planned > 0 && <span>R:R: <strong style={{ color: THEME.colors.blue }}>1:{trade.rr_planned}</strong></span>}
        {trade.lot_size > 0 && <span>Lot: <strong>{trade.lot_size}</strong></span>}
        {trade.exit_price && <span>Exit: <strong style={{ color: THEME.colors.gold, fontFamily: THEME.fonts.hud }}>{trade.exit_price}</strong></span>}
        {trade.pnl_usd !== null && trade.pnl_usd !== undefined && (
          <span>P&L: <strong style={{ color: trade.pnl_usd >= 0 ? THEME.colors.green : THEME.colors.red }}>${trade.pnl_usd}</strong></span>
        )}
        {trade.rr_achieved !== null && trade.rr_achieved !== undefined && (
          <span>Achieved: <strong style={{ color: trade.rr_achieved >= 1.5 ? THEME.colors.green : THEME.colors.red }}>1:{trade.rr_achieved}</strong></span>
        )}
      </div>

      {/* ICT context */}
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: expanded || trade.notes ? '6px' : '0' }}>
        {trade.ict_concept && <span style={S.badge(THEME.colors.blue, 'rgba(0,212,255,0.08)')}>{trade.ict_concept}</span>}
        {trade.htf_bias && <span style={S.badge(trade.htf_bias === 'Bullish' ? THEME.colors.green : THEME.colors.red, 'rgba(255,255,255,0.04)')}>{trade.htf_bias}</span>}
        {trade.confluence_score > 0 && <span style={S.badge(THEME.colors.textMuted, 'rgba(255,255,255,0.04)')}>{trade.confluence_score}/10 confluence</span>}
        {trade.psychology_before && <span style={S.badge(trade.psychology_before >= 7 ? THEME.colors.green : THEME.colors.gold, 'rgba(255,255,255,0.04)')}>Psy {trade.psychology_before}/10</span>}
        {trade.followed_rules === false && <span style={S.badge(THEME.colors.red, 'rgba(255,51,51,0.1)')}>❌ Rules Violated</span>}
      </div>

      {/* Expanded details */}
      {expanded && (
        <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          {trade.notes && <div style={{ fontSize: '12px', color: THEME.colors.textMuted, marginBottom: '6px' }}>📝 {trade.notes}</div>}
          {trade.lessons_learned && <div style={{ fontSize: '12px', color: THEME.colors.blue, marginBottom: '6px' }}>🧠 Lesson: {trade.lessons_learned}</div>}
          {trade.rules_used?.length > 0 && <div style={{ fontSize: '11px', color: THEME.colors.textMuted }}>Rules: {trade.rules_used.join(', ')}</div>}
        </div>
      )}

      {/* Close trade form */}
      {closing && <CloseTradeForm trade={trade} onClosed={() => { setClosing(false); onUpdate(); }} onCancel={() => setClosing(false)} />}
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────────────────
export default function TradeJournal() {
  const [tab, setTab]     = useState('open');
  const [trades, setTrades] = useState(() => getTrades());
  const refresh = () => setTrades(getTrades());

  const open    = trades.filter(t => !t.result || t.result === 'OPEN');
  const closed  = trades.filter(t => t.result && t.result !== 'OPEN');
  const sorted  = [...closed].sort((a, b) => new Date(b.date_closed || b.date) - new Date(a.date_closed || a.date));

  const tabs = [
    { id: 'new',     label: '+ New Trade' },
    { id: 'open',    label: `Open (${open.length})` },
    { id: 'history', label: `History (${closed.length})` },
  ];

  return (
    <div style={S.page}>
      <div style={S.title}>📓 TRADE JOURNAL</div>
      <div style={S.sub}>Log every trade. Win rates auto-update your rules. JARVIS learns from each result.</div>

      <StatsBar trades={trades} />

      <div style={S.tabs}>
        {tabs.map(t => (
          <button key={t.id} style={S.tab(tab === t.id)} onClick={() => setTab(t.id)}>{t.label}</button>
        ))}
      </div>

      {tab === 'new' && (
        <NewTradeForm onSaved={() => { refresh(); setTab('open'); }} />
      )}

      {tab === 'open' && (
        <>
          {open.length === 0
            ? <div style={{ textAlign: 'center', padding: '40px', color: THEME.colors.textMuted }}>No open trades. Log a new trade to track it here.</div>
            : open.map(t => <TradeCard key={t.id} trade={t} onUpdate={refresh} />)
          }
        </>
      )}

      {tab === 'history' && (
        <>
          {sorted.length === 0
            ? <div style={{ textAlign: 'center', padding: '40px', color: THEME.colors.textMuted }}>No closed trades yet.</div>
            : sorted.map(t => <TradeCard key={t.id} trade={t} onUpdate={refresh} />)
          }
        </>
      )}
    </div>
  );
}

